import type { CSSProperties, ReactNode } from 'react';
import { type RobotLifecycleStage } from './StatusBar';
import { Icon } from './Icon';

// design_handoff_kuun_ux/README.md Phase 1 — LifecyclePipeline.
//
// 가로 라이프사이클 카드 행. 6단계 (재고 → 출고 → 설치 → 운영중 → 정비 → 폐기).
// 각 단계 카드는 클릭 시 필터 적용 (활성 단계 toggle). 단계 사이에 chevron-right.
//
// 두 가지 모드:
//   • 'filter' (default) — 클릭 가능, 활성 단계는 tint background + 강조 border
//   • 'progress' — 클릭 불가, 현재 단계만 강조 (RobotDetail Hero 에서 사용)
//
// 디자인 5원칙 중 ④ 도메인 메타포 적용.

export const ROBOT_STAGE_META: Record<
  RobotLifecycleStage,
  { label: string; color: string; tint: string }
> = {
  // docs/work_log/pdf-feedback-2026-05-24 §Phase A —
  // PDF 수정 사항 페이지 3 §1: 라이프사이클 카드 라벨 통일.
  //   재고 → 제조 중 (실 의미: IN_PRODUCTION/IN_STORAGE 단계)
  //   출고 → 출고 대기 (실 의미: READY_TO_SHIP 단계)
  //   설치 → 설치 대기 (실 의미: 출하 후 현장 설치 진행 중)
  // 운영중 / 정비 / 폐기 는 변경 없음.
  stock: {
    label: '제조 중',
    color: 'var(--robot-stock)',
    tint: 'var(--robot-stock-tint)',
  },
  shipping: {
    label: '출고 대기',
    color: 'var(--robot-shipping)',
    tint: 'var(--robot-shipping-tint)',
  },
  installing: {
    label: '설치 대기',
    color: 'var(--robot-installing)',
    tint: 'var(--robot-installing-tint)',
  },
  operating: {
    label: '운영중',
    color: 'var(--robot-operating)',
    tint: 'var(--robot-operating-tint)',
  },
  // 오너 결정 2026-09-18 — '운영중' 은 고객사 실서비스만. 행사·인증·테스트·
  // 사내로 나가 있는 로봇은 여기로 모인다(운영중 바로 옆에 두어 "둘 다 나가
  // 있지만 다른 것" 이 한눈에 보이게).
  other: {
    label: '기타',
    color: 'var(--robot-other)',
    tint: 'var(--robot-other-tint)',
  },
  maintenance: {
    label: '정비',
    color: 'var(--robot-maintenance)',
    tint: 'var(--robot-maintenance-tint)',
  },
  // 2026-09-18 — 종전엔 '제조 중' 카드에 묶여 창고 로봇이 제조 중으로 보였고,
  // 숫자는 아무 카드에도 안 세어졌다.
  storage: {
    label: '재고',
    color: 'var(--robot-storage)',
    tint: 'var(--robot-storage-tint)',
  },
  // round10 요구2 — 폐기 예정(장부 잔존) vs 폐기 완료(상각) 구분.
  retirePlanned: {
    label: '폐기 예정',
    color: 'var(--robot-retire-planned)',
    tint: 'var(--robot-retire-planned-tint)',
  },
  retired: {
    label: '폐기 완료',
    color: 'var(--robot-retired)',
    tint: 'var(--robot-retired-tint)',
  },
};

export interface LifecycleStageData {
  /** Stage key (디자인의 6단계). */
  key: RobotLifecycleStage;
  /** 해당 단계의 row 수. progress 모드에선 무시되거나 1 만 표시. */
  count: number;
  /** 필터 모드에서 active toggle 상태. */
  active?: boolean;
}

interface BaseProps {
  stages: LifecycleStageData[];
  /** 카드 너비 percent (auto-flex). 미사용. */
  /** 헤더 좌측 라벨 (기본 "라이프사이클"). */
  title?: string;
  /** 우측 보조 텍스트 (기본 "클릭하여 필터 적용"). filter 모드에서만. */
  hint?: string;
  /** Hint 텍스트 가운데 정렬 위치 옵션. */
  rightActions?: ReactNode;
  /**
   * 2026-10-21 오너 — "굳이 라이프사이클이라는 네모 뒷배경 없이, 제조 중 > 출고 대기 > 설치 대기… 이런 거만".
   * 바깥 상자 · 제목 · 보조 문구 없이 단계 칸만 그린다(filter 모드).
   */
  bare?: boolean;
}

interface FilterModeProps extends BaseProps {
  mode?: 'filter';
  /** 단계 클릭 시 호출. key 토글. */
  onStageClick?: (key: RobotLifecycleStage) => void;
}

interface ProgressModeProps extends BaseProps {
  mode: 'progress';
  /** 현재 단계 key (active 강조 대상). */
  currentStage: RobotLifecycleStage;
}

export type LifecyclePipelineProps = FilterModeProps | ProgressModeProps;

export function LifecyclePipeline(props: LifecyclePipelineProps): JSX.Element {
  const isProgress = props.mode === 'progress';
  const total = props.stages.reduce((s, x) => s + x.count, 0);

  const containerStyle: CSSProperties = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)',
    borderRadius: 6,
    padding: '12px 14px',
    fontFamily: 'var(--font-sans)',
  };
  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  };
  const titleStyle: CSSProperties = {
    fontSize: 11,
    color: 'var(--fg-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: 500,
  };
  const hintStyle: CSSProperties = {
    fontSize: 10,
    color: 'var(--fg-subtle)',
    fontFamily: 'var(--font-mono)',
  };

  // progress 모드 = 시안 robot-detail.jsx 의 원형 노드 스텝퍼.
  // 완료=brand 채움+check · 현재=operating 채움+내부 dot+펄스 링 · 미래=surface+border-strong 외곽선.
  // 연결선은 지난 구간만 brand. (filter 모드는 기존 박스 행 유지.)
  if (isProgress) {
    const currentStage = (props as ProgressModeProps).currentStage;
    const currentIndex = props.stages.findIndex((s) => s.key === currentStage);
    return (
      <div style={containerStyle} data-testid="lifecycle-pipeline">
        <div style={headerStyle}>
          <span style={titleStyle}>{props.title ?? '라이프사이클'}</span>
          {props.rightActions != null ? (
            props.rightActions
          ) : (
            <span style={hintStyle}>현재: {ROBOT_STAGE_META[currentStage]?.label ?? '?'}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
          {props.stages.map((s, i) => {
            const meta = ROBOT_STAGE_META[s.key];
            // 2026-09-23 — '기타'·'재고' 는 한 줄로 이어지는 단계가 아니라 **곁가지**다. 앞선
            // 칸을 전부 '완료' 로 찍으면 정비·재고·폐기 로봇에 '기타 ✓' 가 찍히고, 기타 로봇에는
            // 설치 대기·운영중 ✓ 가 찍혀 거치지 않은 길을 거친 것처럼 보인다. 곁가지 칸은 현재일
            // 때만 켜고, 현재가 곁가지면 본줄기 완료 표시도 곁가지 앞(출고 대기)까지만 한다.
            const SIDE: ReadonlyArray<string> = ['other', 'storage'];
            const curKey = props.stages[currentIndex]?.key;
            const mainEnd =
              curKey && SIDE.includes(curKey)
                ? props.stages.findIndex((x) => x.key === 'shipping') + 1
                : currentIndex;
            const done = !SIDE.includes(s.key) && i < mainEnd;
            const cur = i === currentIndex;
            // round5 #7d — 현재 노드는 (시안의 고정 초록 대신) 해당 단계의
            // 상태색을 그대로 사용: 출고대기 로봇의 현재 노드가 블루,
            // 설치대기는 앰버로 보여 목록 배지와 어휘가 일치한다.
            const nodeColor = cur ? meta.color : done ? 'var(--brand)' : 'var(--border-strong)';
            return (
              <FragmentLike key={s.key}>
                <button
                  type="button"
                  disabled
                  aria-current={cur ? 'step' : undefined}
                  data-testid={`lifecycle-stage-${s.key}`}
                  data-active={cur}
                  data-done={done}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'default',
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  <span
                    style={{
                      position: 'relative',
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      background: done || cur ? nodeColor : 'var(--bg-surface)',
                      border: `2px solid ${nodeColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {done && (
                      <Icon name="check" size={16} color="var(--brand-on)" strokeWidth={3} />
                    )}
                    {cur && (
                      <span
                        style={{
                          width: 9,
                          height: 9,
                          borderRadius: '50%',
                          background: 'var(--brand-on)',
                        }}
                      />
                    )}
                    {cur && (
                      <span
                        className="kuun-urgent"
                        aria-hidden
                        style={{
                          position: 'absolute',
                          inset: -5,
                          borderRadius: '50%',
                          border: `2px solid ${meta.color}`,
                        }}
                      />
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: cur ? 700 : 500,
                      whiteSpace: 'nowrap',
                      color: cur ? meta.color : done ? 'var(--fg-default)' : 'var(--fg-subtle)',
                    }}
                  >
                    {meta.label}
                  </span>
                </button>
                {i < props.stages.length - 1 && (
                  <div
                    aria-hidden
                    style={{
                      flex: 1,
                      height: 2,
                      background: i < currentIndex ? 'var(--brand)' : 'var(--border-default)',
                      margin: '0 6px',
                      marginBottom: 24,
                    }}
                  />
                )}
              </FragmentLike>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={props.bare ? undefined : containerStyle} data-testid="lifecycle-pipeline">
      {!props.bare && (
        <div style={headerStyle}>
          <span style={titleStyle}>{props.title ?? '라이프사이클'}</span>
          {props.rightActions != null ? (
            props.rightActions
          ) : (
            <span style={hintStyle}>{props.hint ?? '클릭하여 필터 적용'}</span>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
        {props.stages.map((s, i) => {
          const meta = ROBOT_STAGE_META[s.key];
          const active = isProgress
            ? s.key === (props as ProgressModeProps).currentStage
            : !!s.active;
          const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;

          return (
            <FragmentLike key={s.key}>
              <button
                type="button"
                disabled={isProgress}
                aria-pressed={!isProgress ? active : undefined}
                aria-current={isProgress && active ? 'step' : undefined}
                onClick={
                  isProgress ? undefined : () => (props as FilterModeProps).onStageClick?.(s.key)
                }
                data-testid={`lifecycle-stage-${s.key}`}
                data-active={active}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: active ? meta.tint : 'transparent',
                  border: `1px solid ${active ? meta.color : 'var(--border-card)'}`,
                  borderRadius: 5,
                  // 2026-10-21 오너 — 상자 없이(bare) 밝은 바탕 위에 놓이면 칸이 흐려 보였다("잘 보이는 색으로").
                  // 흰 카드 + 테두리(왼쪽 단계 색 띠는 넣었다가 오너 결정으로 뺐다). 2026-10-28 오너 "선 좀 연하게" —
                  // 진한 선과 안쪽 연한 선의 중간 색. 그림자가 있어 칸은 그대로 갈린다.
                  ...(props.bare
                    ? {
                        background: active ? meta.tint : 'var(--bg-surface)',
                        border: `1px solid ${active ? meta.color : 'var(--border-card)'}`,
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                      }
                    : {}),
                  cursor: isProgress ? 'default' : 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 4,
                  textAlign: 'left',
                  fontFamily: 'var(--font-sans)',
                  ...(active && !isProgress ? { boxShadow: `0 0 0 1px ${meta.color} inset` } : {}),
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    width: '100%',
                  }}
                >
                  <span
                    aria-hidden
                    className={isProgress && active ? 'kuun-urgent' : undefined}
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: meta.color,
                    }}
                  />
                  <span
                    style={{
                      fontSize: props.bare ? 12 : 11,
                      color: active ? meta.color : 'var(--fg-default)',
                      fontWeight: active || props.bare ? 600 : 500,
                    }}
                  >
                    {meta.label}
                  </span>
                </div>
                {!isProgress && (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: active ? meta.color : 'var(--fg-default)',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {s.count}
                    </span>
                    {total > 0 && (
                      <span style={{ fontSize: props.bare ? 11 : 10, color: 'var(--fg-muted)' }}>
                        {pct}%
                      </span>
                    )}
                  </div>
                )}
              </button>
              {i < props.stages.length - 1 && (
                <div
                  aria-hidden
                  style={{
                    display: 'grid',
                    placeItems: 'center',
                    padding: '0 4px',
                    color: 'var(--fg-subtle)',
                    fontSize: 14,
                    lineHeight: 1,
                  }}
                >
                  ›
                </div>
              )}
            </FragmentLike>
          );
        })}
      </div>
    </div>
  );
}

// React 의 Fragment 는 key prop 만 받으므로 helper.
function FragmentLike({ children }: { children: ReactNode }): JSX.Element {
  return <>{children}</>;
}
